import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Avatar,
  Fade,
  Tooltip,
  alpha,
  useTheme,
  Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close,
  CheckCircle,
  Circle,
  FilterList,
  Refresh,
  NotificationsNone,
  Info,
  Warning,
  Error
} from '@mui/icons-material';
import { notificationService } from '../services/notificationService';
import { centerService } from '../services/centerService';
import { 
  Notification, 
  NotificationLevel, 
  NotificationModule, 
  UserRole 
} from '../types';
import { barIconButtonSx } from './Layout/barStyles';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    module: '' as NotificationModule | '',
    level: '' as NotificationLevel | ''
  });
  const [selectedCenter, setSelectedCenter] = useState<string>('');
  const [centers, setCenters] = useState<Array<{ _id: string; name: string }>>([]);
  const { user } = useAuth();
  const theme = useTheme();

  const open = Boolean(anchorEl);

  // Helper function to get notification level icon and color
  const getNotificationLevelDetails = (level: NotificationLevel) => {
    switch (level) {
      case NotificationLevel.CRITICAL:
        return { icon: Error, color: theme.palette.error.main, bgColor: alpha(theme.palette.error.main, 0.1) };
      case NotificationLevel.HIGH:
        return { icon: Warning, color: theme.palette.warning.main, bgColor: alpha(theme.palette.warning.main, 0.1) };
      case NotificationLevel.MEDIUM:
        return { icon: Info, color: theme.palette.info.main, bgColor: alpha(theme.palette.info.main, 0.1) };
      default:
        return { icon: Circle, color: theme.palette.text.disabled, bgColor: alpha(theme.palette.text.disabled, 0.1) };
    }
  };



  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  const loadCenters = useCallback(async () => {
    // Only load centers for Super Admin and HOO
    if (user?.isSystemAdmin || false) {
      try {
        const response = await centerService.getCenters();
        setCenters(response?.centers || []);
      } catch (error) {
        console.error('Error loading centers:', error);
      }
    }
  }, [user?.roleId?.name]);

  useEffect(() => {
    // Unread count is loaded on mount only — polling removed. The count
    // refreshes when the user opens the panel (and after marking read/all
    // read), which is enough for the current notification volume. If/when
    // we need near-realtime updates, prefer SSE or a websocket over polling.
    loadUnreadCount();
    loadCenters();
  }, [loadUnreadCount, loadCenters]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, filters, selectedCenter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const filterParams: any = {
        limit: 20  // Show both read and unread
      };
      
      if (filters.module) filterParams.module = filters.module;
      if (filters.level) filterParams.level = filters.level;
      if (selectedCenter) filterParams.centerId = selectedCenter;

      const response = await notificationService.getNotifications(filterParams);
      setNotifications(response.notifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      await notificationService.markAllAsRead();
      await loadNotifications();
      await loadUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };



  const isUserReadNotification = (notification: Notification) => {
    if (!user?._id) {
      return false;
    }
    return notification.readBy.some(read => read.userId === user._id);
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton onClick={handleClick} sx={barIconButtonSx}>
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                px: 0.5,
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
              }
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { 
            width: 420, 
            maxHeight: 650,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={200}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          pb: 1,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Box sx={{
                  backgroundColor: theme.palette.error.main,
                  color: 'white',
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {unreadCount} new
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Filter notifications">
                <IconButton 
                  size="small" 
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    color: showFilters ? theme.palette.primary.main : theme.palette.text.secondary,
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton 
                  size="small" 
                  onClick={loadNotifications}
                  disabled={loading}
                  sx={{ 
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton
                    size="small"
                    onClick={handleMarkAllAsRead}
                    disabled={loading}
                    sx={{ 
                      color: theme.palette.success.main,
                      '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) }
                    }}
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close">
                <IconButton 
                  size="small" 
                  onClick={handleClose}
                  sx={{ 
                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filters */}
          <Fade in={showFilters}>
            <Box sx={{ display: showFilters ? 'block' : 'none' }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Module</InputLabel>
                  <Select
                    value={filters.module}
                    label="Module"
                    onChange={(e) => setFilters(prev => ({ ...prev, module: e.target.value as NotificationModule }))}
                    sx={{ 
                      '& .MuiSelect-select': { fontSize: '0.875rem' }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                    {Object.values(NotificationModule).map(module => (
                      <MenuItem key={module} value={module} sx={{ fontSize: '0.875rem' }}>
                        {module}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Level</InputLabel>
                  <Select
                    value={filters.level}
                    label="Level"
                    onChange={(e) => setFilters(prev => ({ ...prev, level: e.target.value as NotificationLevel }))}
                    sx={{ 
                      '& .MuiSelect-select': { fontSize: '0.875rem' }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                    {Object.values(NotificationLevel).map(level => (
                      <MenuItem key={level} value={level} sx={{ fontSize: '0.875rem' }}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(user?.isSystemAdmin || false) && (
                  <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Center</InputLabel>
                    <Select
                      value={selectedCenter}
                      label="Center"
                      onChange={(e) => setSelectedCenter(e.target.value)}
                      sx={{ 
                        '& .MuiSelect-select': { fontSize: '0.875rem' }
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Centers</MenuItem>
                      {centers.map(center => (
                        <MenuItem key={center._id} value={center._id} sx={{ fontSize: '0.875rem' }}>
                          {center.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>
          </Fade>
        </Box>

        {/* Content */}
        <Box sx={{ height: 500, overflow: 'hidden', width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={32} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading notifications...
                </Typography>
              </Box>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', px: 3 }}>
              <NotificationsNone sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.disabled" textAlign="center">
                You're all caught up! New notifications will appear here.
              </Typography>
            </Box>
          ) : (
            <List sx={{ 
              height: '100%', 
              overflow: 'auto',
              overflowX: 'hidden', // Prevent horizontal scrolling
              p: 0,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.text.primary, 0.2),
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.text.primary, 0.3),
              },
            }}>
              {notifications.map((notification, index) => {
                const isRead = isUserReadNotification(notification);
                const levelDetails = getNotificationLevelDetails(notification.level);
                const LevelIcon = levelDetails.icon;
                
                return (
                  <React.Fragment key={notification._id}>
                    <ListItem
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.03),
                        borderLeft: `3px solid ${isRead ? 'transparent' : levelDetails.color}`,
                        position: 'relative',
                        overflow: 'hidden', // Prevent overflow
                        '&:hover': { 
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'translateX(2px)'
                        },
                        '&:before': !isRead ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '3px',
                          background: `linear-gradient(to bottom, ${levelDetails.color}, ${alpha(levelDetails.color, 0.7)})`
                        } : {}
                      }}
                      onClick={() => !isRead && handleMarkAsRead(notification._id)}
                    >
                      <Box sx={{ display: 'flex', width: '100%', gap: 1.5, overflow: 'hidden' }}>
                        {/* Level Icon */}
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: levelDetails.bgColor,
                            color: levelDetails.color,
                            fontSize: '1rem'
                          }}
                        >
                          <LevelIcon fontSize="small" />
                        </Avatar>

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: isRead ? 500 : 600,
                                color: isRead ? theme.palette.text.secondary : theme.palette.text.primary,
                                lineHeight: 1.3,
                                fontSize: '0.9rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                pr: 1, // Space for unread dot
                                flex: 1
                              }}
                            >
                              {notification.title}
                            </Typography>
                            {!isRead && (
                              <Circle 
                                sx={{ 
                                  fontSize: 8, 
                                  color: theme.palette.primary.main,
                                  mt: 0.5,
                                  flexShrink: 0
                                }} 
                              />
                            )}
                          </Box>

                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 1,
                              lineHeight: 1.4,
                              fontSize: '0.85rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {notification.message}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', overflow: 'hidden', minWidth: 0 }}>
                              {notification.centerName && (
                                <Chip
                                  label={notification.centerName}
                                  size="small"
                                  sx={{
                                    height: isRead ? 24 : 22,
                                    fontSize: isRead ? '0.7rem' : '0.65rem',
                                    fontWeight: 500,
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    '& .MuiChip-label': {
                                      px: isRead ? 1 : 0.75
                                    }
                                  }}
                                />
                              )}
                            </Box>
                            
                            <Typography 
                              variant="caption" 
                              color="text.disabled"
                              sx={{ 
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                flexShrink: 0
                              }}
                            >
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </Typography>
                          </Box>
                        </Box>


                      </Box>
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider sx={{ mx: 2, borderColor: alpha(theme.palette.divider, 0.5) }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;